import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { wasInFlight } from "./fieldJobs";
import { reportSwallowed } from "./reportSwallowed";
import type { FieldJob } from "../types/contract";

// Prefixes of the TanStack Query keys that load a field's screens.
// `invalidateQueries` matches by prefix, so `["preview-cell", fieldId, school]`
// is covered by `["preview-cell", fieldId]`.
export const FIELD_SCREEN_HEADS = [
  "field",
  "layers",
  "covariates",
  "samples",
  "sample-events",
  "soil-composition",
  "haney",
  "accuracy-data",
  "preview-cell",
  "preview-geo",
  "preview-nutrient-underlay",
  "preview-genius-cores",
  "spread-rx",
  "autorx",
  "autorx-served",
  "autorx-geo",
  "data-sources",
  "suggested-core-automap",
  "jd-operations",
  "zone-version",
  "ripple-boundary",
] as const;

export function fieldScreenQueryKeys(fieldId: string): QueryKey[] {
  const keys: QueryKey[] = [["fields"]];
  for (const head of FIELD_SCREEN_HEADS) keys.push([head, fieldId]);
  return keys;
}

// A boundary change feeds the multi-field outline read. Bulk stages feed the
// drop session, which is not keyed by field.
export function extraQueryKeys(processId: string): QueryKey[] {
  if (processId === "boundary_ingest") return [["selection-boundaries"]];
  if (processId.startsWith("bulk_")) {
    return [
      ["bulk-sessions"],
      ["bulk-session"],
      ["bulk-objects"],
      ["bulk-conflicts"],
      ["bulk-errors-badge"],
    ];
  }
  return [];
}

export function invalidateFinishedJobs(
  qc: QueryClient,
  fieldId: string,
  processIds: readonly string[],
): void {
  const keys = fieldScreenQueryKeys(fieldId);
  const seen = new Set<string>();
  for (const processId of processIds) {
    for (const extra of extraQueryKeys(processId)) {
      const mark = JSON.stringify(extra);
      if (seen.has(mark)) continue;
      seen.add(mark);
      keys.push(extra);
    }
  }
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
}

export interface LeftActiveJob {
  fieldId: string;
  jobId: string;
  processId: string;
}

// Jobs that were in flight on the previous active-only snapshot and are absent
// now. The first snapshot is not a finish: there is nothing to compare it to.
export function jobsThatLeftActiveSet(
  prev: Readonly<Record<string, readonly FieldJob[]>> | undefined,
  next: Readonly<Record<string, readonly FieldJob[]>>,
): LeftActiveJob[] {
  if (!prev) return [];
  const still = new Set<string>();
  for (const list of Object.values(next)) {
    for (const job of list) still.add(job.job_id);
  }
  const left: LeftActiveJob[] = [];
  for (const [fieldId, list] of Object.entries(prev)) {
    for (const job of list) {
      if (!wasInFlight(job) || still.has(job.job_id)) continue;
      left.push({
        fieldId,
        jobId: job.job_id,
        processId: job.process_id,
      });
    }
  }
  return left;
}

function groupByField(
  left: readonly LeftActiveJob[],
): Map<string, LeftActiveJob[]> {
  const byField = new Map<string, LeftActiveJob[]>();
  for (const row of left) {
    const list = byField.get(row.fieldId);
    if (list) list.push(row);
    else byField.set(row.fieldId, [row]);
  }
  return byField;
}

async function refreshFieldIfComplete(
  qc: QueryClient,
  fieldId: string,
  rows: readonly LeftActiveJob[],
  fetchFieldJobs: (fieldId: string) => Promise<FieldJob[]>,
  stillCurrent: () => boolean,
): Promise<void> {
  let listed: FieldJob[];
  try {
    listed = await fetchFieldJobs(fieldId);
  } catch (err) {
    reportSwallowed("job completion refresh", err);
    return;
  }
  if (!stillCurrent()) return;
  const statusOf = new Map(listed.map((job) => [job.job_id, job.status]));
  const done = rows.filter((row) => statusOf.get(row.jobId) === "complete");
  if (!done.length || !stillCurrent()) return;
  invalidateFinishedJobs(
    qc,
    fieldId,
    done.map((row) => row.processId),
  );
}

// The org read cannot tell a completion from a cancel. Ask the field read,
// which keeps the finished row, and refresh only when that row is complete.
export async function refreshConfirmedCompletions(
  qc: QueryClient,
  left: readonly LeftActiveJob[],
  fetchFieldJobs: (fieldId: string) => Promise<FieldJob[]>,
  stillCurrent: () => boolean = () => true,
): Promise<void> {
  const groups = groupByField(left);
  await Promise.all(
    [...groups.entries()].map(([fieldId, rows]) =>
      refreshFieldIfComplete(qc, fieldId, rows, fetchFieldJobs, stillCurrent),
    ),
  );
}
